import { supabase } from '@/lib/supabase';
import type { Community, TablesInsert, TablesUpdate } from '@/types';
import { slugify } from '@/lib/utils';

/**
 * Communities for public discovery (Explore + marquee), newest first. Private
 * (invite-only) communities are excluded — they're reachable by direct link but
 * never surfaced in discovery. Owners see their own private ones via
 * `listCommunitiesByOwner`.
 */
export async function listCommunities(limit = 24): Promise<Community[]> {
  const { data, error } = await supabase
    .from('communities')
    .select('*')
    .neq('privacy', 'private')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

/**
 * Real "trending" signal: members who joined each community in the last 24h.
 * Reads the `community_daily_joins` SECURITY DEFINER aggregate (counts only),
 * since `community_members` rows are otherwise private. Returns a map keyed by
 * community id; failures degrade to an empty map (cards then show 0).
 */
export async function listDailyJoins(communityIds: string[]): Promise<Record<string, number>> {
  if (!communityIds.length) return {};
  // The RPC isn't in the generated Functions type yet; cast the call narrowly.
  const { data, error } = await (
    supabase.rpc as unknown as (
      fn: 'community_daily_joins',
      args: { ids: string[] },
    ) => Promise<{ data: { community_id: string; joins: number }[] | null; error: unknown }>
  )('community_daily_joins', { ids: communityIds });
  if (error || !data) return {};
  const map: Record<string, number> = {};
  for (const row of data) map[row.community_id] = row.joins;
  return map;
}

/** Communities owned by a user, newest first. Drives the settings owner tab. */
export async function listCommunitiesByOwner(ownerId: string): Promise<Community[]> {
  const { data, error } = await supabase
    .from('communities')
    .select('*')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getCommunity(id: string): Promise<Community | null> {
  const { data, error } = await supabase
    .from('communities')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Bulk-fetch communities by id (order not guaranteed — callers re-sort). Drives
 *  the "recently viewed" rail, which resolves a list of localStorage ids. */
export async function getCommunitiesByIds(ids: string[]): Promise<Community[]> {
  if (!ids.length) return [];
  const { data, error } = await supabase.from('communities').select('*').in('id', ids);
  if (error) throw error;
  return data ?? [];
}

export async function updateCommunity(id: string, input: TablesUpdate<'communities'>): Promise<Community> {
  const { data, error } = await supabase
    .from('communities')
    .update(input)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export interface CreateCommunityInput {
  name: string;
  /** One-line pitch shown on cards (also mirrored into `description`). */
  tagline?: string;
  category?: string | null;
  access: 'free' | 'paid';
  privacy: 'public' | 'private';
  /** Monthly price in cents; forced to 0 when access is 'free'. */
  priceCents: number;
  /** Hex accent for the community's banner / preview. */
  color?: string;
  /** Banner image (data URL or hosted URL); null/omitted = accent gradient. */
  banner?: string | null;
  ownerId: string;
}

/** Creates a community, slugging the name into a unique `id`. */
export async function createCommunity(input: CreateCommunityInput): Promise<Community> {
  const base = slugify(input.name) || 'community';
  let id = base;
  const tagline = input.tagline?.trim() || null;

  for (let attempt = 0; attempt < 5; attempt++) {
    const candidate = attempt === 0 ? base : `${base}-${Math.random().toString(36).slice(2, 6)}`;
    const row: TablesInsert<'communities'> = {
      id: candidate,
      name: input.name.trim(),
      tagline,
      // Mirror into `description` so cards that read `description ?? tagline`
      // show copy regardless of which field they prefer.
      description: tagline,
      category: input.category ?? null,
      access: input.access,
      privacy: input.privacy,
      price_cents: input.access === 'paid' ? Math.max(input.priceCents, 0) : 0,
      ...(input.color ? { color: input.color } : {}),
      banner: input.banner ?? null,
      owner_id: input.ownerId,
    };
    const { data, error } = await supabase.from('communities').insert(row).select('*').single();
    if (!error) return data;
    if (error.code !== '23505') throw error; // not a unique-violation — bubble up
    id = candidate;
  }

  throw new Error(`Could not generate a unique community id from "${id}"`);
}
